<script setup lang="ts">
import { computed, ref } from "vue";
import { authClient, authErrorMessage } from "../auth-client";

type Mode = "signin" | "signup" | "forgot" | "reset" | "sent";

const props = defineProps<{ notice?: string }>();
const emit = defineEmits<{ authenticated: [] }>();
const query = new URLSearchParams(location.search);
const resetToken = query.get("token") ?? "";
const mode = ref<Mode>(resetToken ? "reset" : "signin");
const name = ref("");
const email = ref("");
const password = ref("");
const confirmPassword = ref("");
const busy = ref(false);
const error = ref("");
const message = ref(props.notice ?? "");
const title = computed(() => ({
  signin: "Welcome back",
  signup: "Start training",
  forgot: "Reset password",
  reset: "Choose a password",
  sent: "Check your email"
})[mode.value]);

function switchMode(next: Mode) {
  mode.value = next;
  error.value = "";
  message.value = "";
  password.value = "";
  confirmPassword.value = "";
}

async function submit() {
  busy.value = true;
  error.value = "";
  message.value = "";
  try {
    if (mode.value === "signin") {
      const result = await authClient.signIn.email({ email: email.value, password: password.value, rememberMe: true });
      if (result.error) throw result.error;
      emit("authenticated");
      return;
    }
    if (mode.value === "signup") {
      if (password.value !== confirmPassword.value) throw new Error("Passwords do not match.");
      const result = await authClient.signUp.email({
        name: name.value,
        email: email.value,
        password: password.value,
        callbackURL: `${location.origin}/?auth=verified`
      });
      if (result.error) throw result.error;
      mode.value = "sent";
      message.value = "We sent a one-time verification link. Open it on this device to finish setup.";
      return;
    }
    if (mode.value === "forgot") {
      const result = await authClient.requestPasswordReset({ email: email.value, redirectTo: `${location.origin}/?auth=reset` });
      if (result.error) throw result.error;
      mode.value = "sent";
      message.value = "If that email has an account, a password-reset link is on its way.";
      return;
    }
    if (mode.value === "reset") {
      if (!resetToken) throw new Error("This password-reset link is missing its token.");
      if (password.value !== confirmPassword.value) throw new Error("Passwords do not match.");
      const result = await authClient.resetPassword({ newPassword: password.value, token: resetToken });
      if (result.error) throw result.error;
      history.replaceState({}, "", "/?auth=password-reset");
      mode.value = "signin";
      message.value = "Password changed. Sign in with the new one.";
      password.value = "";
      confirmPassword.value = "";
    }
  } catch (caught) {
    error.value = caught instanceof Error
      ? caught.message
      : authErrorMessage(caught as { message?: string; code?: string }, "Could not complete authentication.");
  } finally {
    busy.value = false;
  }
}

async function resend() {
  busy.value = true;
  error.value = "";
  const result = await authClient.sendVerificationEmail({ email: email.value, callbackURL: `${location.origin}/?auth=verified` });
  if (result.error) error.value = authErrorMessage(result.error, "Could not resend verification email.");
  else message.value = "Another verification link is on its way.";
  busy.value = false;
}
</script>

<template>
  <main class="login-shell">
    <section class="login-card">
      <div class="brand-mark" aria-hidden="true"><i></i><span></span><i></i></div>
      <p class="eyebrow">Your training, without the tapping</p>
      <h1>{{ title }}</h1>
      <p class="login-copy">
        {{ mode === "signup" ? "A fast workout log, an honest history, and a planning bridge for your AI coach." : "Two movements in view. Every working set remembered. Your next week ready for Codex." }}
      </p>

      <div v-if="message" class="auth-message" role="status">{{ message }}</div>
      <template v-if="mode === 'sent'">
        <button v-if="email" class="button ghost full" :disabled="busy" @click="resend">Resend verification</button>
        <button class="text-link" @click="switchMode('signin')">Back to sign in</button>
      </template>
      <form v-else @submit.prevent="submit">
        <label v-if="mode === 'signup'">Name<input v-model="name" autocomplete="name" required maxlength="100" autofocus></label>
        <label v-if="mode !== 'reset'">Email<input v-model="email" type="email" inputmode="email" autocomplete="email" autocapitalize="none" required :autofocus="mode !== 'signup'"></label>
        <label v-if="mode === 'signin'">Password<input v-model="password" type="password" autocomplete="current-password" required></label>
        <template v-if="mode === 'signup' || mode === 'reset'">
          <label>New password<input v-model="password" type="password" autocomplete="new-password" minlength="10" maxlength="128" required></label>
          <label>Confirm password<input v-model="confirmPassword" type="password" autocomplete="new-password" minlength="10" maxlength="128" required></label>
          <small class="field-help">At least 10 characters. A passphrase works well.</small>
        </template>
        <p v-if="error" class="form-error" role="alert">{{ error }}</p>
        <button class="button primary full" :disabled="busy">
          {{ busy ? "Working…" : mode === "signin" ? "Sign in" : mode === "signup" ? "Create account" : mode === "forgot" ? "Send reset link" : "Set new password" }}
        </button>
      </form>

      <div v-if="mode === 'signin'" class="auth-alternates">
        <button class="text-link" @click="switchMode('forgot')">Forgot password?</button>
        <button class="text-link" @click="switchMode('signup')">Create an account</button>
      </div>
      <button v-else-if="mode !== 'sent' && mode !== 'reset'" class="text-link" @click="switchMode('signin')">Back to sign in</button>
      <p class="privacy-note"><span></span> Verified email · revocable sessions · private athlete data</p>
    </section>
  </main>
</template>
